/**
 * The theme registry. A theme is a CSS file in src/styles/themes/ (imported by main.tsx) plus an
 * entry here; the pre-paint script in index.html keeps its own copy of the ids and colors.
 */
import type { IconName } from "@/components";

export const THEMES = [
  { id: "light", label: "Light", icon: "sun", description: "Crisp, cool neutrals (default).", metaColor: "#f6f9f9" },
  { id: "dark", label: "Dark", icon: "moon", description: "Graphite, for dim rooms.", metaColor: "#121717" },
  {
    id: "paper",
    label: "Paper",
    icon: "page",
    description: "Warm and low-glare for long sessions.",
    metaColor: "#f1ede4",
  },
] as const satisfies ReadonlyArray<{
  id: string;
  label: string;
  icon: IconName;
  description: string;
  metaColor: string;
}>;

export type ThemeId = (typeof THEMES)[number]["id"];
/** What the user picked: a theme, or "system" to follow prefers-color-scheme (light or dark). */
export type ThemeChoice = ThemeId | "system";

// Persisted in browsers (and read by the inline script in index.html): a new key would reset everyone's theme.
export const THEME_STORAGE_KEY = "perfume.theme";
export const SYSTEM_DARK_QUERY = "(prefers-color-scheme: dark)";

export function isThemeChoice(value: unknown): value is ThemeChoice {
  return value === "system" || THEMES.some((theme) => theme.id === value);
}

export function readStoredThemeChoice(): ThemeChoice | null {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemeChoice(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function storeThemeChoice(choice: ThemeChoice): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, choice);
  } catch {
    // Private browsing can refuse storage; the choice still applies for this visit.
  }
}

export function resolveTheme(choice: ThemeChoice, systemPrefersDark: boolean): ThemeId {
  if (choice === "system") return systemPrefersDark ? "dark" : "light";
  return choice;
}

/** Sets data-theme on <html> and updates <meta name="theme-color">. */
export function applyTheme(theme: ThemeId): void {
  document.documentElement.dataset.theme = theme;
  const meta = document.querySelector('meta[name="theme-color"]');
  const color = THEMES.find((candidate) => candidate.id === theme)?.metaColor;
  if (meta && color) meta.setAttribute("content", color);
}
